// components/portal/BrandIdFiche.tsx
// A brand's Overview facts: its one-sentence business, then the precise values of every line we
// enrich (website, founded, network, positioning, category, surface, footprint, revenue,
// contacts, legal). Read-only, it reads the Brand off the kernel registry (syncing it in
// if absent) and reuses the same value formatters as the Source enrich panel/agent.
'use client';

import React, { useEffect } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { readCampaignRow } from '#lib/leasing/campaignRow';
import { activeCampaignResearch, researchingFieldsOf } from '#lib/leasing/researchPass';
import { formatFinancialValue, formatCnccSector, formatSurface, hostnameOf } from '#lib/leasing/enrichNote';
import { formatCnccPath } from '#lib/leasing/cnccReferential';

type BrandFiche = {
  name?: string;
  logoUrl?: string | null;
  websiteUrl?: string | null;
  summary?: string | null;
  description?: string | null;
  positioningNote?: string | null;
  category?: string | null;
  cnccSector?: string | null;
  cnccSubFamily?: string | null;
  cnccActivity?: string | null;
  declaredNetworkSize?: number | null;
  foundedYear?: number | null;
  surfaceStandard?: number | null;
  surfaceMin?: number | null;
  surfaceMax?: number | null;
  geographicFootprint?: string | null;
  operatingCountryCodes?: string[];
  sortableTotalRevenue?: number | null;
  revenueIsEstimate?: boolean | null;
  revenueEntityName?: string | null;
  revenueEntityCountry?: string | null;
  /** Modelled, not measured. Rendered as its own row so the two never read alike. */
  estimatedRevenue?: { revenue: number | null; year?: number | null; inputs?: Record<string, unknown> | null } | null;
  contactIds?: string[];
  legalEntityCount?: number | null;
};

// "FOOD_AND_BEVERAGE" -> "Food and beverage"
const formatEnum = (v?: string | null): string | null =>
  v ? (v.charAt(0) + v.slice(1).toLowerCase()).replace(/_/g, ' ') : null;

const LABELS = {
  en: { website: 'Website', founded: 'Founded', network: 'Stores', positioning: 'Positioning', category: 'Category', surface: 'Surface', footprint: 'Footprint', revenue: 'Revenue', contacts: 'Contacts', legal: 'Legal entities', stores: 'stores', noData: 'No brand data yet.' },
  fr: { website: 'Site web', founded: 'Création', network: 'Magasins', positioning: 'Positionnement', category: 'Catégorie', surface: 'Surface', footprint: 'Présence', revenue: 'Chiffre d’affaires', contacts: 'Contacts', legal: 'Entités légales', stores: 'pdv', noData: 'Aucune donnée marque.' },
} as const;

export const BrandIdFiche = ({ brandId, lang }: { brandId?: string | null; lang: string }) => {
  const { kernel } = useKernel();
  const brand = useKernelStore((s) =>
    brandId ? (s.inventory.Brand.registry[brandId] as BrandFiche | undefined) : undefined
  );

  useEffect(() => {
    if (kernel.current && brandId && !brand) void kernel.current.syncEntity('Brand', brandId);
  }, [kernel, brandId, brand]);

  const enriching = useKernelStore((state) => (brandId ? researchingFieldsOf(state.sourceEnrichingDims[brandId], activeCampaignResearch(readCampaignRow(state.inventory, state.sourceSelectedCampaignId)), brandId) : undefined));

  if (!brandId) return null;
  const t = LABELS[lang === 'fr' ? 'fr' : 'en'];

  const positioning = brand?.positioningNote || null;

  const surface = brand ? formatSurface(brand) : null;
  const footprint =
    brand?.geographicFootprint?.trim() ||
    (brand?.operatingCountryCodes && brand.operatingCountryCodes.length > 0 ? brand.operatingCountryCodes.join(', ') : null);
  // CNCC funnel first (the referential the leasing teams share), Fater category as fallback.
  const categoryVal = (brand && formatCnccPath(brand)) || formatCnccSector(brand?.cnccSector) || formatEnum(brand?.category);
  // One-line business summary: the team's fastest read on an unknown brand.
  const summary = brand?.summary?.trim() || brand?.description?.trim() || null;
  const revenue = brand ? formatFinancialValue(brand, lang) : null;
  const contactsN = brand?.contactIds?.length ?? 0;
  const legalN = brand?.legalEntityCount ?? 0;

  // Wide values (the footprint sentence, the CNCC path) take the whole line; the rest share it.
  type Row = { key: string; label: string; value: React.ReactNode; wide?: boolean };
  const rows: Row[] = [];
  if (brand?.websiteUrl) {
    rows.push({
      key: 'website', label: t.website,
      value: (
        <a href={brand.websiteUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#ECECEC] underline decoration-white/20 underline-offset-4 hover:decoration-white">
          {hostnameOf(brand.websiteUrl)}
        </a>
      ),
    });
  }
  if (typeof brand?.declaredNetworkSize === 'number') rows.push({ key: 'network', label: t.network, value: `${brand.declaredNetworkSize.toLocaleString()} ${t.stores}` });
  if (revenue) rows.push({ key: 'revenue', label: t.revenue, value: revenue });
  if (surface) rows.push({ key: 'surface', label: t.surface, value: surface });
  if (positioning) rows.push({ key: 'positioning', label: t.positioning, value: positioning, wide: true });
  if (typeof brand?.foundedYear === 'number') rows.push({ key: 'founded', label: t.founded, value: String(brand.foundedYear) });
  // The modelled-estimate row is HIDDEN (owner's call, 2026-09-19), matching SourceBrandPanel
  // and the Revenus tab: the fiche shows filed and declared figures only. The estimate still
  // feeds scoring; un-hiding is restoring one row here.
  if (contactsN > 0) rows.push({ key: 'contacts', label: t.contacts, value: String(contactsN) });
  if (legalN > 0) rows.push({ key: 'legal', label: t.legal, value: String(legalN) });
  if (categoryVal) rows.push({ key: 'category', label: t.category, value: categoryVal, wide: true });
  if (footprint) rows.push({ key: 'footprint', label: t.footprint, value: footprint, wide: true });
  // A field being researched that holds nothing yet still gets its row, so the card has a place
  // to show the work (the card scrolls to it) instead of the value appearing from nowhere.
  const PLACEHOLDERS: Array<[string, string]> = [
    ['website', t.website], ['network', t.network], ['positioning', t.positioning],
    ['category', t.category], ['surface', t.surface], ['legal', t.legal],
  ];
  for (const [key, label] of PLACEHOLDERS) {
    if (enriching?.includes(key) && !rows.some((r) => r.key === key)) rows.push({ key, label, value: '…' });
  }

  // First principles (owner, 2026-09-30: 'rethink the brand details for maximum readability'): the
  // row above already shows the logo and the name, so this is what the brand DOES in one sentence,
  // then its facts as a document reads them: a grey label over a value, three to a line, no card.
  return (
    <div className="@container w-full" data-enrich-field="logo">
      {summary ? <p className="max-w-[720px] text-[#D0D0D0]" style={{ fontSize: 15, lineHeight: '24px' }}>{summary}</p> : null}
      {rows.length > 0 ? (
        <dl className={`grid grid-cols-2 gap-x-8 gap-y-5 @[640px]:grid-cols-3${summary ? ' mt-6' : ''}`}>
          {rows.map((r) => (
            <div key={r.key} data-enrich-field={r.key} className={`min-w-0${r.wide ? ' col-span-2 @[640px]:col-span-3' : ''}`}>
              <dt className="text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>{r.label}</dt>
              <dd className={`mt-1 break-words text-[#ECECEC]${enriching?.includes(r.key) ? ' thinking-text-animated' : ''}`} style={{ fontSize: 15, lineHeight: '22px' }}>{r.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-[#8E8E8E]" style={{ fontSize: 14 }}>{t.noData}</p>
      )}
    </div>
  );
};
